import { Autocomplete, CircularProgress, TextField } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";

function ZoomableImagePreview({ imagenFactura, emptyMessage, emptyDescription }) {
    const containerRef = useRef(null);
    const [zoom, setZoom] = useState(1);
    const [offset, setOffset] = useState({ x: 0, y: 0 });
    const [dragState, setDragState] = useState({
        isDragging: false,
        startX: 0,
        startY: 0,
        originX: 0,
        originY: 0,
    });

    useEffect(() => {
        const containerElement = containerRef.current;

        if (!containerElement) {
            return undefined;
        }

        const handleWheel = (event) => {
            if (!imagenFactura) return;

            event.preventDefault();

            const zoomStep = event.deltaY < 0 ? 0.12 : -0.12;
            setZoom((currentZoom) => {
                const nextZoom = currentZoom + zoomStep;
                const normalizedZoom = Math.min(3, Math.max(1, Number(nextZoom.toFixed(2))));

                if (normalizedZoom === 1) {
                    setOffset({ x: 0, y: 0 });
                }

                return normalizedZoom;
            });
        };

        containerElement.addEventListener("wheel", handleWheel, { passive: false });

        return () => {
            containerElement.removeEventListener("wheel", handleWheel);
        };
    }, [imagenFactura]);

    const handleMouseDown = (event) => {
        if (!imagenFactura || zoom <= 1) return;

        setDragState({
            isDragging: true,
            startX: event.clientX,
            startY: event.clientY,
            originX: offset.x,
            originY: offset.y,
        });
    };

    const handleMouseMove = (event) => {
        if (!dragState.isDragging || zoom <= 1) return;

        const deltaX = event.clientX - dragState.startX;
        const deltaY = event.clientY - dragState.startY;

        setOffset({
            x: dragState.originX + deltaX,
            y: dragState.originY + deltaY,
        });
    };

    const handleMouseUp = () => {
        setDragState((currentState) => ({
            ...currentState,
            isDragging: false,
        }));
    };

    const handleMouseLeave = () => {
        handleMouseUp();
        setZoom(1);
        setOffset({ x: 0, y: 0 });
    };

    return (
        <div
            ref={containerRef}
            className="group relative min-h-[360px] w-full overflow-hidden rounded-2xl border border-gray-200 bg-[#f1f5f9] shadow-sm lg:min-h-[480px]"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
        >
            {imagenFactura ? (
                <>
                    <img
                        src={imagenFactura}
                        alt="imagen obsequio"
                        draggable={false}
                        className={`h-full w-full object-cover transition-transform duration-200 ease-out select-none ${zoom > 1 ? (dragState.isDragging ? "cursor-grabbing" : "cursor-grab") : "group-hover:cursor-zoom-in"}`}
                        style={{
                            transform: `scale(${zoom}) translate(${offset.x / zoom}px, ${offset.y / zoom}px)`,
                            transformOrigin: "center center",
                        }}
                    />

                    <div className="pointer-events-none absolute inset-x-4 bottom-4 rounded-xl bg-black/55 px-3 py-2 text-xs text-white opacity-0 transition-opacity duration-200 group-hover:opacity-100">
                        Usa la rueda del mouse para acercar o alejar la imagen. Arrastra para moverla.
                    </div>
                </>
            ) : (
                <div className="flex h-full flex-col items-center justify-center p-6 text-center">
                    <p className="text-base font-semibold text-gray-700">{emptyMessage}</p>
                    {emptyDescription ? (
                        <p className="mb-5 text-sm text-gray-500">{emptyDescription}</p>
                    ) : null}
                </div>
            )}
        </div>
    );
}

function AddObsequiosComponents({
    id,
    mode,
    dataObsequio,
    loadingGuardar,

    handleGuardar,
    handleCancelar,
    handleRechazarFactura,

    imagenFactura,
    fechaFactura,
    setFechaFactura,
    montoTotal,
    setMontoTotal,
    nroFactura,
    setNroFactura,
    timbrado,
    setTimbrado,

    motivoRechazo,
    setMotivoRechazo,

    dataLocales, loadingCargarLocales, localesSelected, handleSearchLocal, handleLocalSelected,
}) {

    const [viewRechazo, setViewRechazo] = useState(false);

    const arrayOpciones = [
        {
            value: "Monto minimo no alcanzado",
            label: "Monto mínimo no alcanzado",
        },
        {
            value: "La factura está ilegible",
            label: "La factura está ilegible",
        },
        {
            value: "La factura no correponde a ningun local del complejo",
            label: "La factura no correponde a ningún local del complejo",
        },
        {
            value: "El local no esta adherido a la promocion",
            label: "El local no esta adherido a la promoción",
        },
    ]


    return (
        <div className="rounded-xl bg-white p-6 shadow-xl">
            <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
                <div>
                    {mode === "view" ? (
                        <ZoomableImagePreview
                            imagenFactura={imagenFactura}
                            emptyMessage="No hay imagen"
                            emptyDescription="Seleccione una imagen para el obsequio"
                        />
                    ) : (
                        <ZoomableImagePreview
                            imagenFactura={imagenFactura}
                            emptyMessage="No hay imagen"
                        />
                    )}
                </div>

                <div className="grid content-start grid-cols-1 gap-4 self-start">
                    {!viewRechazo && (
                        <div className="grid content-start grid-cols-1 gap-4 self-start">
                            <div>
                                <label className="mb-1.5 block text-sm font-semibold text-gray-700">Local</label>
                                <Autocomplete
                                    disablePortal
                                    options={dataLocales}
                                    fullWidth
                                    loading={loadingCargarLocales}
                                    loadingText={"Cargando..."}
                                    noOptionsText={"No hay opciones"}
                                    value={localesSelected || null}
                                    disabled={mode === "view"}
                                    getOptionLabel={(option) => {
                                        if (!option) return "";
                                        const name = option?.nombre_fantasia || "Sin nombre";
                                        const ruc = option?.ruc ? ` - ${option.ruc}` : "";
                                        return name + ruc;
                                    }}
                                    isOptionEqualToValue={(option, value) => option?.id === value?.id}
                                    onInputChange={(event, newInputValue, reason) => {
                                        if (reason === "input") {
                                            handleSearchLocal(newInputValue);
                                        }
                                    }}
                                    onChange={(event, value) => {
                                        handleLocalSelected(value);
                                    }}
                                    // getOptionLabel={(option) => {
                                    //     if (!option) return "";
                                    //     const name = option?.user?.first_name && option?.user?.last_name
                                    //         ? `${option.user.first_name} ${option.user.last_name}`
                                    //         : option?.user?.email || "";
                                    //     const cedula = option?.ci ? ` - ${option?.ci}` : "";
                                    //     return name + cedula;
                                    // }}
                                    renderOption={(props, option) => (
                                        <li {...props} key={option?.id}>
                                            <div className="flex flex-col">
                                                <span className="text-sm text-gray-800 font-medium">
                                                    {option?.nombre_fantasia || "Sin nombre"}
                                                </span>
                                                <span className="text-xs text-gray-500">
                                                    {option?.ruc || "-"}
                                                </span>
                                            </div>
                                        </li>
                                    )}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Seleccione"
                                            size="small"
                                            sx={{
                                                '& .MuiOutlinedInput-root': {
                                                    backgroundColor: '#f1f5f9',
                                                    borderRadius: '0.5rem',
                                                    fontSize: '0.875rem',
                                                },
                                            }}
                                            InputProps={{
                                                ...params.InputProps,
                                                endAdornment: (
                                                    <React.Fragment>
                                                        {loadingCargarLocales ? <CircularProgress color="inherit" size={20} /> : null}
                                                        {params.InputProps.endAdornment}
                                                    </React.Fragment>
                                                ),
                                            }}
                                        />
                                    )}
                                />
                            </div>


                            <div>
                                <label className="mb-1.5 block text-sm font-semibold text-gray-700">Nro de factura</label>
                                <input
                                    type="text"
                                    value={nroFactura}
                                    onChange={(e) => setNroFactura(e.target.value)}
                                    autoCapitalize="none"
                                    autoCorrect="off"
                                    disabled={mode === "view"}
                                    className="w-full rounded-lg border border-gray-200 bg-[#f1f5f9] px-3 py-2.5 text-sm outline-none transition focus:border-blue-200 focus:ring-2 focus:ring-blue-200"
                                />
                            </div>


                            <div>
                                <label className="mb-1.5 block text-sm font-semibold text-gray-700">Timbrado</label>

                                <input
                                    type="text"
                                    value={timbrado}
                                    onChange={(e) => setTimbrado(e.target.value)}
                                    autoCapitalize="none"
                                    autoCorrect="off"
                                    disabled={mode === "view"}
                                    className="w-full rounded-lg border border-gray-200 bg-[#f1f5f9] px-3 py-2.5 text-sm outline-none transition focus:border-blue-200 focus:ring-2 focus:ring-blue-200"
                                />
                            </div>

                            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                                <div>
                                    <label className="mb-1.5 block text-sm font-semibold text-gray-700">Monto total</label>
                                    <input
                                        type="text"
                                        value={montoTotal ? Number(montoTotal).toLocaleString('es-PY', { style: 'currency', currency: 'PYG' }) : ''}
                                        //permitir solo numeros
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            // Permitir solo números y eliminar cualquier otro carácter
                                            const numericValue = value.replace(/[^0-9]/g, '');
                                            setMontoTotal(numericValue);
                                        }}
                                        disabled={mode === "view"}
                                        className="w-full rounded-lg border border-gray-200 bg-[#f1f5f9] px-3 py-2.5 text-sm outline-none transition focus:border-blue-200 focus:ring-2 focus:ring-blue-200"
                                    />
                                </div>

                                <div>
                                    <label className="mb-1.5 block text-sm font-semibold text-gray-700">Fecha factura</label>
                                    <input
                                        type="date"
                                        value={fechaFactura}
                                        onChange={(e) => setFechaFactura(e.target.value)}
                                        disabled={mode === "view"}
                                        className="w-full rounded-lg border border-gray-200 bg-[#f1f5f9] px-3 py-2.5 text-sm outline-none transition focus:border-blue-200 focus:ring-2 focus:ring-blue-200"
                                    />
                                </div>


                            </div>
                        </div>
                    )}

                    {viewRechazo && (
                        <div className="grid content-start grid-cols-1 gap-4 self-start">
                            <p className="text-sm text-gray-500">
                                Debes ingresar un motivo de rechazo para poder rechazar el obsequio. Este motivo será visible para el cliente, por lo que se recomienda ser claro y específico en la explicación del rechazo.
                            </p>

                            <div>
                                <label className="mb-1.5 block text-sm font-semibold text-gray-700">
                                    Motivo de rechazo
                                </label>

                                <select
                                    value={motivoRechazo}
                                    onChange={(e) => setMotivoRechazo(e.target.value)}
                                    disabled={mode === "view"}
                                    className="w-full rounded-lg border border-gray-200 bg-[#f1f5f9] px-3 py-2.5 text-sm outline-none transition focus:border-blue-200 focus:ring-2 focus:ring-blue-200"
                                >
                                    <option value="">Seleccione un tipo</option>
                                    {arrayOpciones.map((tipo) => (
                                        <option key={tipo.value} value={tipo.value}>
                                            {tipo.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* <div>
                                <label className="mb-1.5 block text-sm font-semibold text-gray-700">Motivo de rechazo</label>
                                <textarea
                                    rows={4}
                                    // value={dataObsequio?.motivo_rechazo || ""}
                                    // onChange={(e) => setDataObsequio((prev) => ({ ...prev, motivo_rechazo: e.target.value }))}
                                    autoCapitalize="none"
                                    autoCorrect="off"
                                    className="w-full rounded-lg border border-gray-200 bg-[#f1f5f9] px-3 py-2.5 text-sm outline-none transition focus:border-blue-200 focus:ring-2 focus:ring-blue-200"
                                />
                            </div> */}

                        </div>
                    )}




                    {dataObsequio?.estado === "pendiente" && (
                        <div className="mt-6 flex flex-wrap items-center gap-4 border-t border-gray-200 pt-6">
                            {!viewRechazo && (
                                <div className="flex gap-4">
                                    {mode !== "view" && (
                                        <button
                                            type="button"
                                            onClick={handleGuardar}
                                            className="rounded-lg bg-[#104063] px-6 py-2 font-medium text-white transition-colors hover:bg-[#0d3553]"
                                            disabled={loadingGuardar}
                                        >
                                            Guardar
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={handleCancelar}
                                        className="rounded-lg bg-gray-100 px-6 py-2 font-medium text-gray-700 transition-colors hover:bg-gray-200"
                                    >
                                        Cancelar
                                    </button>
                                </div>
                            )}



                            {viewRechazo && (
                                <div className="flex gap-4">
                                    {mode !== "view" && (
                                        <button
                                            type="button"
                                            onClick={handleRechazarFactura}
                                            className="rounded-lg bg-[#104063] px-6 py-2 font-medium text-white transition-colors hover:bg-[#0d3553]"
                                            disabled={loadingGuardar}
                                        >
                                            Guardar rechazo
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => setViewRechazo(false)}
                                        className="rounded-lg bg-gray-100 px-6 py-2 font-medium text-gray-700 transition-colors hover:bg-gray-200"
                                    >
                                        Cancelar
                                    </button>
                                </div>
                            )}

                            {(mode !== "view" && !viewRechazo) && (
                                <button
                                    type="button"
                                    onClick={() => setViewRechazo(true)}
                                    className="ml-auto rounded-lg bg-red-600 px-6 py-2 font-medium text-white transition-colors hover:bg-red-700"
                                >
                                    Rechazar
                                </button>
                            )}
                        </div>
                    )}
                </div>





            </div>
        </div>
    );
}

export default AddObsequiosComponents;